import type { ThreadChunkElement, ThreadState } from "@/entities/thread";
import type { PublicArchiveSnapshot } from "@/entities/thread/archiveSnapshot";
import {
  normalizeThreadChunkAttachments,
  normalizeThreadStateAttachments,
} from "@/entities/thread/normalizeAttachments";
import {
  getThreadChunkStaleTime,
  THREAD_CHUNK_QUERY_BEHAVIOR,
  threadArchiveSnapshotQueryKey,
  threadChunkQueryKey,
  threadPrivateChunkQueryKey,
  threadStateQueryKey,
} from "@/features/thread/hooks/threadChunkQuery";
import { ApiError } from "@/shared/api/errors";
import { API_VERSION } from "@/shared/api/version";

export type ThreadFetcher = <T>(
  path: string,
  options: {
    allowTokenIssue?: boolean;
    boardKey?: string;
    cache?: RequestCache;
    requiresToken?: boolean;
  },
) => Promise<T>;

type ThreadQueryOptions<TData, TQueryKey extends readonly unknown[]> = {
  queryFn: () => Promise<TData>;
  queryKey: TQueryKey;
};

type PublicThreadChunkQueryOptions = ThreadQueryOptions<
  ThreadChunkElement[],
  ReturnType<typeof threadChunkQueryKey>
> & {
  refetchOnWindowFocus: false;
  retry: (failureCount: number, error: Error) => boolean;
  staleTime: (query: {
    state: { data: ThreadChunkElement[] | undefined };
  }) => number;
};

type ThreadStateRequest = {
  options:
    | { cache: "no-store" }
    | {
        allowTokenIssue: false;
        cache: "no-store";
        requiresToken: true;
      };
  path: string;
};

export function createThreadStateRequest(
  threadId: string,
  after: number,
  hasViewerToken: boolean,
): ThreadStateRequest {
  return {
    path: `/threads/${threadId}/state?after=${encodeURIComponent(after)}`,
    options: hasViewerToken
      ? {
          allowTokenIssue: false,
          cache: "no-store",
          requiresToken: true,
        }
      : { cache: "no-store" },
  };
}

/**
 * カタログセルのhover先読み用。`useThread` の初回マウント時
 * (`latestAcceptedSeqRef.current === 0`) と同じキー・同じリクエストを組み立てる。
 * 受け入れ済みレスがある状態からの再取得は先読みの対象外。
 */
export function createThreadStatePrefetchOptions(
  threadId: string,
  viewerContext: number,
  fetcher: ThreadFetcher,
): ThreadQueryOptions<ThreadState, ReturnType<typeof threadStateQueryKey>> {
  return {
    queryKey: threadStateQueryKey(threadId, viewerContext),
    queryFn: (): Promise<ThreadState> => {
      const request = createThreadStateRequest(threadId, 0, true);
      return fetcher<ThreadState>(request.path, request.options).then(
        normalizeThreadStateAttachments,
      );
    },
  };
}

export function createArchiveSnapshotQueryOptions(
  threadId: string,
  boardSlug: string,
  fetcher: ThreadFetcher,
): ThreadQueryOptions<
  PublicArchiveSnapshot,
  ReturnType<typeof threadArchiveSnapshotQueryKey>
> {
  return {
    queryKey: threadArchiveSnapshotQueryKey(threadId, boardSlug),
    queryFn: (): Promise<PublicArchiveSnapshot> =>
      fetcher<PublicArchiveSnapshot>(
        `/archive/${encodeURIComponent(boardSlug)}/threads/${encodeURIComponent(threadId)}/snapshot.json`,
        { boardKey: boardSlug },
      ),
  };
}

export function createThreadChunkQueryOptions(
  threadId: string,
  chunkNumber: number,
  fetcher: ThreadFetcher,
): PublicThreadChunkQueryOptions {
  return {
    queryKey: threadChunkQueryKey(threadId, chunkNumber),
    queryFn: (): Promise<ThreadChunkElement[]> =>
      fetcher<ThreadChunkElement[]>(
        `/threads/${threadId}/public-chunks/${chunkNumber}?apiVersion=${encodeURIComponent(API_VERSION)}`,
        {},
      ).then(normalizeThreadChunkAttachments),
    retry: (failureCount: number, error: Error) =>
      !isPublicThreadChunkNotFound(error) && failureCount < 1,
    staleTime: (query: { state: { data: ThreadChunkElement[] | undefined } }) =>
      getThreadChunkStaleTime(query.state.data),
    ...THREAD_CHUNK_QUERY_BEHAVIOR,
  };
}

export function createPrivateThreadChunkQueryOptions(
  threadId: string,
  viewerContext: number,
  chunkNumber: number,
  fetcher: ThreadFetcher,
): ThreadQueryOptions<
  ThreadChunkElement[],
  ReturnType<typeof threadPrivateChunkQueryKey>
> {
  return {
    queryKey: threadPrivateChunkQueryKey(threadId, viewerContext, chunkNumber),
    queryFn: (): Promise<ThreadChunkElement[]> =>
      fetcher<ThreadChunkElement[]>(
        `/threads/${threadId}/chunks/${chunkNumber}`,
        {
          allowTokenIssue: false,
          cache: "no-store",
          requiresToken: true,
        },
      ).then(normalizeThreadChunkAttachments),
  };
}

export function isPublicThreadChunkNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function isArchiveSnapshotNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
